import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { Lock, Plus, Trash2 } from "lucide-react";
import CustomDropdown, { DropdownOption } from "../Ui_Components/CustomDropdown";
import { ReactComponent as EditIcon } from "../../assects/svg/client_details/Edit_Icon.svg";
import { ReactComponent as ProfileImage } from "../../assects/svg/client_details/Profile_picture_of_head_nutrinist.svg";
import { BASE_URL, FIREBASE_COLLECTIONS } from "../api/url";
import {
  assignPrimaryNutritionist,
  assignBackupNutritionist,
  getAvailableNutritionistsList,
  getAssignedNutritionists,
} from "../../services/sales";
import { getProfile } from "../../services/profile";
import { useAuth } from "../../context/auth/AuthContext";
import { chatService } from "../../services/chatService";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../config/firebase";
import { Button } from "../Ui_Components/Button";

// ─── Types ────────────────────────────────────────────────────────────────────
interface Nutritionist {
  id: string;
  name: string;
  role: string;
  specializations: string[];
  languages: string[];
  avatarUrl?: string;
  avatarColor?: string;
}

interface ApiNutritionist {
  nutrist_id: number;
  nutrist_title?: string;
  nutrist_fname: string;
  nutrist_lname: string;
  nutrist_profile?: string;
  nutrist_assign_role?: string;
  nutrist_lang1?: string;
  nutrist_lang2?: string;
  nutrist_lang3?: string;
  nutrist_lang4?: string;
  nutrist_specialisation_details?: { specialisation: string; spec_exp: string }[];
  nutrist_interest_zone?: { interest_zone: string }[];
}

interface AssignedNutritionistEntry {
  nutritionist_id: number;
  nutrist_fname: string;
  nutrist_lname: string;
  nutrist_assign_role: string;
  primary_status: string | null;
  backup_status: string | null;
}

// ─── Avatar colour palette (cycled when no profile image) ────────────────────
const AVATAR_COLORS = ["#E07B54", "#5B8DEF", "#7BC67E", "#B07FE0", "#0DB5B5", "#F5A623", "#E53E3E", "#805AD5"];

function mapApiNutritionist(n: ApiNutritionist, index: number): Nutritionist {
  const avatarUrl = n.nutrist_profile ? `${BASE_URL}${n.nutrist_profile}` : undefined;

  const languages = [n.nutrist_lang1, n.nutrist_lang2, n.nutrist_lang3, n.nutrist_lang4]
    .filter((l): l is string => Boolean(l && l.trim()));

  const fromSpec = (n.nutrist_specialisation_details ?? [])
    .map((s) => s.specialisation)
    .filter((s) => Boolean(s && s.trim()));
  const fromZone = (n.nutrist_interest_zone ?? [])
    .map((z) => z.interest_zone)
    .filter((z) => Boolean(z && z.trim()));
  const specializations = fromSpec.length > 0 ? fromSpec : fromZone;

  return {
    id: String(n.nutrist_id),
    name: `${n.nutrist_fname.trim()} ${n.nutrist_lname.trim()}`,
    role: n.nutrist_assign_role?.trim() ?? "",
    specializations,
    languages,
    avatarUrl,
    avatarColor: avatarUrl ? undefined : AVATAR_COLORS[index % AVATAR_COLORS.length],
  };
}

// ─── Static Head Nutritionist ─────────────────────────────────────────────────
const HEAD_NUTRITIONIST: Nutritionist = {
  id: "head",
  name: "Deepika Chalasani",
  role: "Head nutritionist",
  specializations: ["Weight Management", "Gut Health", "Diabetes"],
  languages: ["Telugu", "Hindi", "English"],
};

// ─── Canvas text measurement ──────────────────────────────────────────────────
let _canvas: HTMLCanvasElement | null = null;
function measureTextWidth(text: string, font: string): number {
  if (!_canvas) _canvas = document.createElement("canvas");
  const ctx = _canvas.getContext("2d")!;
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
interface TooltipProps {
  items: string[];
  mouse: { x: number; y: number };
  visible: boolean;
}

const ItemTooltip: React.FC<TooltipProps> = ({ items, mouse, visible }) => {
  if (!visible) return null;
  const gap = 12;
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: mouse.y + gap,
        left: mouse.x,
        zIndex: 9999,
        border: "0.5px solid #F3F4F6",
        pointerEvents: "none",
      }}
      className="bg-white rounded-lg px-2 py-2 shadow-sm inline-flex max-w-[240px]"
    >
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="text-[8px] 2xl:text-[10px] font-inter px-3 py-1 rounded-full whitespace-nowrap"
            style={{ backgroundColor: "#F0F0F0", color: "#1F1F1F" }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>,
    document.body
  );
};

// ─── TruncatedList ────────────────────────────────────────────────────────────
const TruncatedList: React.FC<{ items: string[] }> = ({ items }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [hovered, setHovered] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const isTruncated = visibleCount < items.length;

  useLayoutEffect(() => {
    if (!containerRef.current || items.length === 0) return;

    const getFont = () =>
      window.innerWidth >= 1920 ? "16px Inter" : "14px Inter";

    const calculate = () => {
      const containerWidth = containerRef.current!.offsetWidth;
      if (!containerWidth) return;

      const font = getFont();
      const sepWidth = measureTextWidth(", ", font);
      let usedWidth = 0;
      let count = 0;

      for (let i = 0; i < items.length; i++) {
        const itemWidth = measureTextWidth(items[i], font);
        const remaining = items.length - i - 1;
        const badgeWidth =
          remaining > 0 ? measureTextWidth(` +${remaining}`, font) : 0;
        const ahead = i === 0 ? 0 : sepWidth;

        if (usedWidth + ahead + itemWidth + badgeWidth <= containerWidth) {
          usedWidth += ahead + itemWidth;
          count = i + 1;
        } else break;
      }

      setVisibleCount(Math.max(1, count));
    };

    calculate();

    const ro = new ResizeObserver(calculate);
    ro.observe(containerRef.current!);
    return () => ro.disconnect();
  }, [items]);

  const visible = items.slice(0, visibleCount);
  const remaining = items.length - visibleCount;

  return (
    <>
      <span
        ref={containerRef}
        onMouseEnter={() => isTruncated && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}
        className={`block w-full overflow-hidden whitespace-nowrap text-[#1F1F1F] text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-inter ${
          isTruncated ? "cursor-default" : ""
        }`}
      >
        {visible.join(", ")}
        {remaining > 0 && (
          <span className="text-[#0DB5B5] font-medium"> +{remaining}</span>
        )}
      </span>
      <ItemTooltip items={items} mouse={mouse} visible={hovered && isTruncated} />
    </>
  );
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar: React.FC<{ nutritionist: Nutritionist; size?: number }> = ({
  nutritionist,
  size = 40,
}) => {
  if (nutritionist.avatarUrl) {
    return (
      <img
        src={nutritionist.avatarUrl}
        alt={nutritionist.name}
        style={{ width: size, height: size }}
        className="rounded-full object-cover flex-shrink-0"
      />
    );
  }
  const initial = nutritionist.name.charAt(0).toUpperCase();
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: nutritionist.avatarColor || "#0DB5B5",
      }}
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold text-sm"
    >
      {initial}
    </div>
  );
};

// ─── Assign Nutritionist Popup ────────────────────────────────────────────────
interface AssignPopupProps {
  isOpen: boolean;
  onClose: () => void;
  type: "primary" | "backup";
  initialId?: string;
  onAssign: (nutritionist: Nutritionist | null) => void;
  nutritionists: Nutritionist[];
  userId: number;
  adminId: number;
}

const AssignPopup: React.FC<AssignPopupProps> = ({
  isOpen,
  onClose,
  type,
  initialId = "",
  onAssign,
  nutritionists,
  userId,
  adminId,
}) => {
  const [selectedId, setSelectedId] = useState(initialId);
  const [isSaving, setIsSaving] = useState(false);
  const selected = nutritionists.find((n) => n.id === selectedId) || null;
  const isEditMode = Boolean(initialId);

  const dropdownOptions: DropdownOption[] = nutritionists.map((n) => ({
    value: n.id,
    label: n.name,
    subtitle: n.role,
  }));

  useEffect(() => {
    if (isOpen) setSelectedId(initialId);
  }, [isOpen, initialId]);

  const handleSearch = (term: string) => {
    if (term === "") setSelectedId("");
  };

  const sendNutritionistAssignedNotification = async () => {
    try {
      const userDocRef = doc(db, FIREBASE_COLLECTIONS.USERS, `userid_${userId}`);
      const userDoc = await getDoc(userDocRef);
      if (!userDoc.exists()) return;
      const playerId: string | undefined = userDoc.data()?.playerId;
      if (!playerId) return;
      await fetch('https://api.onesignal.com/notifications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_ONESIGNAL_API_KEY as string,
        },
        body: JSON.stringify({
          app_id: process.env.REACT_APP_ONESIGNAL_APP_ID,
          include_player_ids: [playerId],
          headings: { en: '🎉 Nutritionist Assigned!' },
          contents: { en: 'Your personal nutritionist is assigned—let\'s begin!' },
          data: { type: 'nutritionist_assigned' },
        }),
      });
    } catch (err) {
      console.error('Error sending nutritionist assigned notification:', err);
    }
  };

  const handleSave = async () => {
    if (!isEditMode && !selected) return;
    setIsSaving(true);
    try {
      const data = await assignPrimaryNutritionist({
        user_id: userId,
        nutritionist_id: selected ? Number(selected.id) : null,
        admin_id: adminId,
      });
      if (Number(data.success) === 1) {
        chatService.createOrUpdateChatGroup(userId).catch(() => {});
        if (!isEditMode && selected) sendNutritionistAssignedNotification();
        onAssign(selected);
        setSelectedId("");
        onClose();
      }
    } catch {
      // Network error — silently ignore, keep popup open
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setSelectedId("");
    onClose();
  };

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  if (!isOpen) return null;

  const title = type === "primary"
    ? (isEditMode ? "Edit primary nutritionist" : "Add primary nutritionist")
    : (isEditMode ? "Edit backup nutritionist"  : "Add backup nutritionist");

  const canSave = (Boolean(selected) || isEditMode) && !isSaving;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-[9998]"
        onClick={handleClose}
      />
      <div
        className="fixed z-[9999] bg-white rounded-xl shadow-md flex flex-col"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800,
          maxWidth: "calc(100vw - 32px)",
        }}
      >
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-[15px] font-semibold font-inter text-[#1F1F1F]">
            {title}
          </h2>
        </div>

        <div className="h-px bg-[#E8E8E8]" />

        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-[220px] flex-shrink-0">
              <CustomDropdown
                options={dropdownOptions}
                value={selectedId}
                onChange={setSelectedId}
                onSearch={handleSearch}
                placeholder="Select"
                searchable
                zIndex={10000}
                renderOption={(option) => {
                  const nut = nutritionists.find((n) => n.id === option.value);
                  return (
                    <div className="flex items-center gap-2">
                      {nut && <Avatar nutritionist={nut} size={26} />}
                      <span className="text-sm font-inter text-[#1F1F1F] truncate">
                        {option.label}
                      </span>
                    </div>
                  );
                }}
              />
            </div>

            <div className="w-[120px] flex-shrink-0">
              <span className="text-sm font-inter text-[#1F1F1F]">
                {selected?.role ? selected.role : (
                  <span className="text-[#AEAEB2]">Role</span>
                )}
              </span>
            </div>

            <div className="flex-1 min-w-0">
              {selected && selected.specializations.length > 0 ? (
                <TruncatedList items={selected.specializations} />
              ) : (
                <span className="text-sm font-inter text-[#AEAEB2]">Specialisation</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {selected && selected.languages.length > 0 ? (
                <TruncatedList items={selected.languages} />
              ) : (
                <span className="text-sm font-inter text-[#AEAEB2]">Language</span>
              )}
            </div>
          </div>
        </div>

        <div className="h-px bg-[#E8E8E8]" />

        <div className="flex items-center justify-end gap-3 px-6 py-4">
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? "Saving..." : "Save & add"}
          </Button>
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Nutritionist Row ─────────────────────────────────────────────────────────
const NutritionistRow: React.FC<{
  nutritionist: Nutritionist;
  onEdit?: () => void;
}> = ({ nutritionist, onEdit }) => (
  <div className="flex items-center gap-3 px-4 py-3">
    <Avatar nutritionist={nutritionist} size={32} />

    <div className="w-[260px] xl:w-[300px] 3xl:w-[360px] flex-shrink-0">
      <p className="text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-semibold font-inter text-[#1F1F1F] truncate">
        {nutritionist.name}
      </p>
    </div>

    <div className="w-[120px] xl:w-[150px] 3xl:w-[180px] flex-shrink-0">
      <p className="text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-inter text-[#1F1F1F]">{nutritionist.role}</p>
    </div>

    <div className="flex-1 min-w-0">
      <TruncatedList items={nutritionist.specializations} />
    </div>

    <div className="flex-1 min-w-0">
      <TruncatedList items={nutritionist.languages} />
    </div>

    {onEdit && (
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
      >
        <EditIcon className="w-4 h-4" />
      </button>
    )}
  </div>
);

// ─── Section Card ─────────────────────────────────────────────────────────────
interface SectionCardProps {
  label: string;
  optional?: boolean;
  assigned: Nutritionist | null;
  onPlusClick: () => void;
  onEdit: () => void;
  readOnly?: boolean;
}

const SectionCard: React.FC<SectionCardProps> = ({
  label,
  optional,
  assigned,
  onPlusClick,
  onEdit,
  readOnly,
}) => (
  <div
    className={`border border-[#E0E0E0] rounded-lg bg-white transition-colors ${readOnly ? "" : "cursor-pointer hover:border-[#0DB5B5]"}`}
    onClick={readOnly ? undefined : (assigned ? onEdit : onPlusClick)}
  >
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-[9px] md:text-[10px] xl:text-[11px] 3xl:text-xs font-inter font-semibold tracking-widest text-[#646464] uppercase">
        {label}
        {optional && (
          <span className="ml-1.5 text-[#8E8E93] normal-case font-normal tracking-normal">
            (optional)
          </span>
        )}
      </span>
      {!readOnly && !assigned && (
        <button
          onClick={(e) => { e.stopPropagation(); onPlusClick(); }}
          className="w-7 h-7 xl:w-8 xl:h-8 rounded-md bg-[#0DB5B5] flex items-center justify-center hover:bg-[#0CA0A0] transition-colors flex-shrink-0"
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
            <path
              d="M7 2.5V11.5M2.5 7H11.5"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>

    {assigned && (
      <NutritionistRow nutritionist={assigned} onEdit={readOnly ? undefined : onEdit} />
    )}
  </div>
);

// ─── Backup Assign Popup ──────────────────────────────────────────────────────
interface BackupRow {
  rowId: string;
  selectedId: string;
}

interface BackupAssignPopupProps {
  isOpen: boolean;
  onClose: () => void;
  initialNutritionists: Nutritionist[];
  onSave: (nutritionists: Nutritionist[]) => void;
  nutritionists: Nutritionist[];
  userId: number;
  adminId: number;
  excludeId?: string;
}

const BackupAssignPopup: React.FC<BackupAssignPopupProps> = ({
  isOpen,
  onClose,
  initialNutritionists,
  onSave,
  nutritionists,
  userId,
  adminId,
  excludeId,
}) => {
  const [rows, setRows] = useState<BackupRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDeleteRowId, setConfirmDeleteRowId] = useState<string | null>(null);

  const dropdownOptions: DropdownOption[] = nutritionists
    .filter((n) => n.id !== excludeId)
    .map((n) => ({
      value: n.id,
      label: n.name,
      subtitle: n.role,
    }));

  useEffect(() => {
    if (isOpen) {
      setConfirmDeleteRowId(null);
      if (initialNutritionists.length > 0) {
        setRows(initialNutritionists.map((n) => ({ rowId: `${n.id}-${Date.now()}`, selectedId: n.id })));
      } else {
        setRows([{ rowId: `new-${Date.now()}`, selectedId: "" }]);
      }
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen]);

  if (!isOpen) return null;

  const addRow = () => {
    setConfirmDeleteRowId(null);
    setRows((prev) => [...prev, { rowId: `new-${Date.now()}`, selectedId: "" }]);
  };

  const removeRow = (rowId: string) => {
    setConfirmDeleteRowId(null);
    setRows((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const updateRow = (rowId: string, selectedId: string) => {
    setConfirmDeleteRowId(null);
    setRows((prev) => prev.map((r) => r.rowId === rowId ? { ...r, selectedId } : r));
  };

  const handleTrashClick = (rowId: string) => {
    if (confirmDeleteRowId === rowId) {
      removeRow(rowId);
    } else {
      setConfirmDeleteRowId(rowId);
    }
  };

  const handleSave = async () => {
    const selected = rows
      .map((r) => nutritionists.find((n) => n.id === r.selectedId))
      .filter(Boolean) as Nutritionist[];
    if (selected.length === 0) return;
    setIsSaving(true);
    try {
      const data = await assignBackupNutritionist({
        user_id: userId,
        nutritionist_ids: selected.map((n) => Number(n.id)),
        admin_id: adminId,
      });
      if (data?.data?.all_backup_nutritionists) {
        const serverList: Nutritionist[] = (data.data.all_backup_nutritionists as { nutritionist_id: number }[])
          .map((s) => nutritionists.find((n) => n.id === String(s.nutritionist_id)))
          .filter(Boolean) as Nutritionist[];
        onSave(serverList);
      } else {
        onSave(selected);
      }
      chatService.createOrUpdateChatGroup(userId).catch(() => {});
      onClose();
    } catch {
      // Network error — silently ignore, keep popup open
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = rows.some((r) => r.selectedId !== "") && !isSaving;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-[9998]"
        onClick={() => { setConfirmDeleteRowId(null); onClose(); }}
      />
      <div
        className="fixed z-[9999] bg-white rounded-xl shadow-md flex flex-col"
        style={{
          top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          width: 800, maxWidth: "calc(100vw - 32px)",
        }}
      >
        <div className="px-6 pt-5 pb-4">
          <h2 className="text-[15px] font-semibold font-inter text-[#1F1F1F]">
            Add backup nutritionist
          </h2>
        </div>

        <div className="h-px bg-[#E8E8E8]" />

        <div className="px-6 py-4 flex flex-col gap-3 max-h-[360px] overflow-y-auto">
          {rows.map((row) => {
            const nut = nutritionists.find((n) => n.id === row.selectedId) || null;
            const isConfirming = confirmDeleteRowId === row.rowId;

            if (isConfirming) {
              return (
                <div
                  key={row.rowId}
                  className="flex items-center justify-between px-4 py-[4px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md"
                >
                  <span className="text-sm font-inter text-[#1F1F1F]">
                    Delete{" "}
                    <span className="font-semibold">
                      "{nut ? nut.name : "this entry"}"
                    </span>
                  </span>
                  <button
                    onClick={() => removeRow(row.rowId)}
                    className="p-1.5 rounded hover:bg-red-100 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4 text-[#E53E3E]" />
                  </button>
                </div>
              );
            }

            return (
              <div
                key={row.rowId}
                className="flex items-center gap-4"
                onClick={() => { if (confirmDeleteRowId) setConfirmDeleteRowId(null); }}
              >
                <div className="w-[220px] flex-shrink-0">
                  <CustomDropdown
                    options={dropdownOptions}
                    value={row.selectedId}
                    onChange={(val) => updateRow(row.rowId, val)}
                    onSearch={(term) => { if (term === "") updateRow(row.rowId, ""); }}
                    placeholder="Select"
                    searchable
                    zIndex={10000}
                    renderOption={(option) => {
                      const n = nutritionists.find((x) => x.id === option.value);
                      return (
                        <div className="flex items-center gap-2">
                          {n && <Avatar nutritionist={n} size={26} />}
                          <span className="text-sm font-inter text-[#1F1F1F] truncate">{option.label}</span>
                        </div>
                      );
                    }}
                  />
                </div>

                <div className="w-[120px] flex-shrink-0">
                  <span className="text-sm font-inter text-[#1F1F1F]">
                    {nut?.role ? nut.role : <span className="text-[#AEAEB2]">Role</span>}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  {nut && nut.specializations.length > 0
                    ? <TruncatedList items={nut.specializations} />
                    : <span className="text-sm font-inter text-[#AEAEB2]">Specialisation</span>
                  }
                </div>

                <div className="flex-1 min-w-0">
                  {nut && nut.languages.length > 0
                    ? <TruncatedList items={nut.languages} />
                    : <span className="text-sm font-inter text-[#AEAEB2]">Language</span>
                  }
                </div>

                <button
                  onClick={() => handleTrashClick(row.rowId)}
                  className="flex-shrink-0 p-1.5 rounded hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-[#E53E3E]" />
                </button>
              </div>
            );
          })}

          <button
            onClick={addRow}
            className="flex items-center gap-1.5 text-[#0DB5B5] text-sm font-inter font-medium hover:opacity-80 transition-opacity w-fit mt-1"
          >
            <Plus className="w-4 h-4" />
            Add backup nutritionist
          </button>
        </div>

        <div className="h-px bg-[#E8E8E8]" />

        <div className="flex items-center justify-end gap-3 px-6 py-4">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? "Saving..." : "Save & add"}
          </Button>
        </div>
      </div>
    </>,
    document.body
  );
};

// ─── Backup Section Card ──────────────────────────────────────────────────────
const BackupSectionCard: React.FC<{
  assigned: Nutritionist[];
  onPlusClick: () => void;
  readOnly?: boolean;
}> = ({ assigned, onPlusClick, readOnly }) => (
  <div
    className={`border border-[#E0E0E0] rounded-lg bg-white transition-colors ${readOnly ? "" : "cursor-pointer hover:border-[#0DB5B5]"}`}
    onClick={readOnly ? undefined : onPlusClick}
  >
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-[9px] md:text-[10px] xl:text-[11px] 3xl:text-xs font-inter font-semibold tracking-widest text-[#646464] uppercase">
        Backup Nutritionist
        <span className="ml-1.5 text-[#8E8E93] normal-case font-normal tracking-normal">(optional)</span>
      </span>
      {!readOnly && (
        assigned.length > 0 ? (
          <button
            onClick={(e) => { e.stopPropagation(); onPlusClick(); }}
            className="flex-shrink-0 p-1.5 rounded hover:bg-gray-100 transition-colors"
          >
            <EditIcon className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onPlusClick(); }}
            className="w-7 h-7 xl:w-8 xl:h-8 rounded-md bg-[#0DB5B5] flex items-center justify-center hover:bg-[#0CA0A0] transition-colors flex-shrink-0"
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M7 2.5V11.5M2.5 7H11.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        )
      )}
    </div>
    {assigned.map((nut) => (
      <NutritionistRow key={nut.id} nutritionist={nut} />
    ))}
  </div>
);

// ─── AssignNutritionistTab ─────────────────────────────────────────────────────
const AssignNutritionistTab: React.FC<{ userId: number; readOnly?: boolean }> = ({ userId, readOnly }) => {
  const { user } = useAuth();
  const adminId = user?.nutrist_id ?? 0;
  const [nutritionists, setNutritionists] = useState<Nutritionist[]>([]);

  const [primaryNutritionist, setPrimaryNutritionist] = useState<Nutritionist | null>(null);
  const [backupNutritionists, setBackupNutritionists] = useState<Nutritionist[]>([]);

  const [primaryPopupOpen, setPrimaryPopupOpen] = useState(false);
  const [primaryEditingId, setPrimaryEditingId] = useState("");
  const [backupPopupOpen, setBackupPopupOpen] = useState(false);

  useEffect(() => {
    Promise.all([getAvailableNutritionistsList(), getAssignedNutritionists(userId)])
      .then(async ([availableData, assignedData]) => {
        let availableList: Nutritionist[] = [];
        if (availableData.success && Array.isArray(availableData.data)) {
          availableList = availableData.data.map(mapApiNutritionist);
          setNutritionists(availableList);
        }

        const getFullProfile = async (
          nutritionist_id: number,
          idx: number
        ): Promise<Nutritionist | null> => {
          const cached = availableList.find((n) => n.id === String(nutritionist_id));
          if (cached) return cached;
          try {
            const data = await getProfile(nutritionist_id);
            /* GET_PROFILE returns the nutritionist on data.data (single object).
               Older array shape kept as a fallback. */
            const nutData: ApiNutritionist | undefined =
              data.data ?? data.nutritionists?.[0];
            if (!nutData) return null;
            return mapApiNutritionist(nutData, idx);
          } catch {
            return null;
          }
        };

        if (Number(assignedData.success) !== 1 || !Array.isArray(assignedData.nutritionists)) return;
        const assignedList: AssignedNutritionistEntry[] = assignedData.nutritionists;

        const primaryEntry = assignedList.find((n) => n.primary_status === "1");
        const backupEntries = assignedList.filter(
          (n) => n.backup_status !== null && n.nutritionist_id !== primaryEntry?.nutritionist_id
        );

        if (primaryEntry) {
          const profile = await getFullProfile(primaryEntry.nutritionist_id, 0);
          if (profile) setPrimaryNutritionist(profile);
        }

        if (backupEntries.length > 0) {
          const profiles = await Promise.all(
            backupEntries.map((e, i) => getFullProfile(e.nutritionist_id, i))
          );
          const valid = profiles.filter(Boolean) as Nutritionist[];
          setBackupNutritionists(valid);
        }
      })
      .catch(() => {
        // Silently fail — sections stay empty
      });
  }, [userId]);

  return (
    <div className="px-6 pb-6 pt-4">
      <div className="flex flex-col gap-4">
        {/* Head nutritionist card — static */}
        <h2 className="text-[11px] md:text-[12px] xl:text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">Assign Nutritionist</h2>
        <div className="bg-white border border-[#E0E0E0] rounded-lg flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <ProfileImage className="w-8 h-8 md:w-9 md:h-9 xl:w-10 xl:h-10 rounded-full flex-shrink-0" />
            <div>
              <p className="text-[11px] md:text-[12px] xl:text-[13px] 3xl:text-sm font-semibold font-inter text-[#1F1F1F]">{HEAD_NUTRITIONIST.name}</p>
              <p className="text-[10px] md:text-[11px] xl:text-[12px] 3xl:text-xs text-[#646464] font-inter mt-0.5">{HEAD_NUTRITIONIST.role}</p>
            </div>
          </div>
          <div className="w-7 h-7 xl:w-8 xl:h-8 rounded-md border border-[#E0E0E0] flex items-center justify-center flex-shrink-0">
            <Lock className="w-3 h-3 xl:w-4 xl:h-4 text-[#646464]" />
          </div>
        </div>

        {/* Primary nutritionist section */}
        <SectionCard
          label="Primary Nutritionist"
          assigned={primaryNutritionist}
          onPlusClick={() => { setPrimaryEditingId(""); setPrimaryPopupOpen(true); }}
          onEdit={() => { setPrimaryEditingId(primaryNutritionist?.id ?? ""); setPrimaryPopupOpen(true); }}
          readOnly={readOnly}
        />

        {/* Backup nutritionist section */}
        <BackupSectionCard
          assigned={backupNutritionists}
          onPlusClick={() => setBackupPopupOpen(true)}
          readOnly={readOnly}
        />
      </div>

      <AssignPopup
        isOpen={primaryPopupOpen}
        onClose={() => setPrimaryPopupOpen(false)}
        type="primary"
        initialId={primaryEditingId}
        onAssign={(nut) => setPrimaryNutritionist(nut)}
        nutritionists={nutritionists}
        userId={userId}
        adminId={adminId}
      />

      <BackupAssignPopup
        isOpen={backupPopupOpen}
        onClose={() => setBackupPopupOpen(false)}
        initialNutritionists={backupNutritionists}
        onSave={setBackupNutritionists}
        nutritionists={nutritionists}
        userId={userId}
        adminId={adminId}
        excludeId={primaryNutritionist?.id}
      />
    </div>
  );
};

export default AssignNutritionistTab;
